import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { CardMode } from '@components/card/card.component';
import { MdsCheckboxComponent, MdsTextFieldComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Site } from '@models/site';
import { SupportTier } from '@models/study';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { rxjsComputed, rxjsEffect } from '@utility/utility';
import { combineLatest, filter, map, switchMap, tap } from 'rxjs';
import { FormErrorsComponent } from '../../form-errors/form-errors.component';
import { StipendVisitsComponent } from '../stipend-visits/stipend-visits.component';
import { SiteServicesSpecificServicesFormGroup } from './site-services-specific-services.form';

@Component({
    selector: 'medpace-site-services-specific-services',
    imports: [AsyncPipe, MdsCheckboxComponent, StipendVisitsComponent, FormErrorsComponent, MdsTextFieldComponent],
    templateUrl: './site-services-specific-services.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteServicesSpecificServicesComponent {
    userService = inject(UserServiceV2);
    elevatedAccess = this.userService.signalPermissions.site.access.elevated;
    adminRequestService = inject(AdminRequestServices);
    studyService = inject(StudyStateService);

    mode = input.required<CardMode>();
    mode$ = toObservable(this.mode);
    formGrp = input.required<SiteServicesSpecificServicesFormGroup>();
    formGrp$ = toObservable(this.formGrp);

    site = input<Site>();
    site$ = toObservable(this.site).pipe(filter(Boolean));
    study$ = this.studyService.getStudy().pipe(filter(Boolean));

    patch$ = rxjsEffect(() =>
        combineLatest({
            site: this.site$,
            formGroup: this.formGrp$,
        }).pipe(
            tap(({ formGroup, site }) => {
                formGroup.patchValue({
                    travelSupportControl: site.services.travel,
                    reimbursementControl: site.services.reimbursement,
                    stipendControl: site.services.stipend,
                    otherControl: site.services.other,
                    otherDetails: site.services.otherDetails,
                });
            })
        )
    );

    travelRequestsPresent$ = rxjsComputed(() => {
        return this.site$.pipe(
            filter((site) => !!site.id),
            switchMap((site) =>
                this.adminRequestService.getTravelRequestCountSite(site.id).pipe(map((count) => count > 0))
            )
        );
    });

    setTravelSupportUneditable$ = rxjsEffect(() =>
        combineLatest({
            travelRequestsPresent: this.travelRequestsPresent$.source$,
            formGroup: this.formGrp$,
            mode: this.mode$,
        }).pipe(
            filter(({ mode }) => mode === 'edit'),
            tap(({ travelRequestsPresent, formGroup }) => {
                if (travelRequestsPresent) {
                    formGroup.controls.travelSupportControl.disable({ persistent: true });
                }
            })
        )
    );

    setSupportTier$ = rxjsEffect(() =>
        combineLatest({
            study: this.study$,
            formGroup: this.formGrp$,
            mode: this.mode$,
        }).pipe(
            filter(({ mode }) => mode === 'edit'),
            tap(({ formGroup, study }) => {
                if (study.supportTier === SupportTier.payment) {
                    formGroup.controls.travelSupportControl.disable({ persistent: true });
                }
            })
        )
    );
    reimbursement$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.reimbursementControl.valueChangesStartWithCurrentValue())
        )
    );
    stipend$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.stipendControl.valueChangesStartWithCurrentValue())
        )
    );
    travel$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.travelSupportControl.valueChangesStartWithCurrentValue())
        )
    );

    other$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.otherControl.valueChangesStartWithCurrentValue())
        )
    );
    switchOtherControl = rxjsEffect(() =>
        combineLatest({
            mode: this.mode$,
            reimbursement: this.reimbursement$.source$,
            stipend: this.stipend$.source$,
            travel: this.travel$.source$,
            formGroup: this.formGrp$,
        }).pipe(
            filter(({ mode }) => mode === 'edit'),
            tap(({ formGroup, reimbursement, stipend, travel }) => {
                if (reimbursement || stipend || travel) {
                    formGroup.controls.otherControl.enable({ breakPersistence: true });
                } else {
                    formGroup.controls.otherControl.setValue(false);
                    formGroup.controls.otherControl.disable({ persistent: true });
                }
            })
        )
    );

    stipendVisitsComponent = viewChild.required<StipendVisitsComponent>(StipendVisitsComponent);

    push(site: Site) {
        const value = this.formGrp().getRawValue();
        site.services.travel = value.travelSupportControl ?? false;
        site.services.reimbursement = value.reimbursementControl ?? false;
        site.services.stipend = value.stipendControl ?? false;
        site.services.other = value.otherControl ?? false;
        site.services.otherDetails = value.otherControl ? (value.otherDetails ?? '') : '';

        if (value.stipendControl) {
            site = this.stipendVisitsComponent().push(site);
        } else {
            site.siteVisitStipends = [];
        }
        return site;
    }
}
